import React, { useEffect, useMemo, useState } from 'react';
import { Content, FilterSpacer, Shadow } from './style';
import { Button, Input, Modal, Space } from 'antd';
import { DeleteOutlined, ExclamationCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { DateSelector } from '../../../shared/components/DateSelector';
import { PastMeetingsFilter } from '../PastMeetingsFilter';
import { usePastMeetings } from '../../../meetings/past-meetings/past-meetings.hook';
import { useDebouncedCallback } from 'use-debounce';
import { PAST_MEETINGS_PAGE_KEY } from '../../../meetings/past-meetings/past-meetings.store';
import { coreAPI } from '../../../../API/core';
import { useToast } from '../../../../hooks/useToast';
import { FiltersPopover } from '../../../saved-filters/components/FiltersPopover';
import { MeetingParticipantsSelector } from '../../../insights/portfolio-insights/components/MeetingParticipantsSelector';

const { confirm } = Modal;

const { Search } = Input;

const PastMeetingsFilterBar: React.FC = () => {
  const {
    searchTerm,
    isFetching,
    dateRange,
    dateRangeType,
    selectedMeetingsTo,
    reset,
    changeSearchTerm,
    changeDateRange,
    clearSelectedMeetingsToRemove,
    refetch,
    saveFiltersView,
    setCurrentFilterView,
    changeDateRangeType,
    setParticipants,
  } = usePastMeetings();

  const { success, error } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [inputSearchTerm, setInputSearchTerm] = useState('');
  const [dateOption, setDateOption] = useState<string>(dateRangeType);

  useEffect(() => {
    if (dateRangeType !== dateOption) {
      setDateOption(dateRangeType);
    }
  }, [dateRangeType]);

  useEffect(() => {
    changeDateRangeType(dateOption);
  }, [dateOption]);

  const searchDebounce = useDebouncedCallback(value => {
    changeSearchTerm(value);
  }, 400);

  useEffect(() => {
    setInputSearchTerm(searchTerm);
  }, [searchTerm]);

  const updateFilters = ({ key, value }: { key: string; value: string }) => {
    const currentFilters = JSON.parse(localStorage.getItem('pageFilters') || '{}');
    const currentPageFilters = currentFilters[PAST_MEETINGS_PAGE_KEY] || {};
    localStorage.setItem(
      'pageFilters',
      JSON.stringify({
        ...currentFilters,
        [PAST_MEETINGS_PAGE_KEY]: { ...currentPageFilters, [key]: value },
      }),
    );
  };

  useEffect(() => {
    if (dateRangeType !== dateOption) {
      updateFilters({ key: 'dateRangeType', value: dateOption });
    }
  }, [dateOption]);

  const handleChangeInputText = (text: string) => {
    setInputSearchTerm(text);
    searchDebounce(text);
  };

  const totalMeetingsSelected = useMemo(() => {
    return new Set([...selectedMeetingsTo.remove]).size;
  }, [selectedMeetingsTo]);

  const onRemoveSelectedRecaps = () => {
    setIsLoading(true);
    const coreClient = new coreAPI();
    coreClient
      .removeRecaps([...selectedMeetingsTo.remove])
      .then(() => {
        clearSelectedMeetingsToRemove();
        success('Recaps removed successfully');
        refetch();
      })
      .catch(() => {
        error(`Failed to remove selected events.`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Content>
      <FilterSpacer>
        <Shadow>
          <Search
            placeholder="Search by title, account, or participants"
            onChange={e => handleChangeInputText(e.target.value)}
            style={{ width: 350 }}
            value={inputSearchTerm}
          />
          <DateSelector
            direction={'past'}
            value={dateOption}
            onDateOptionChanged={setDateOption}
            onDateRangeChanged={changeDateRange}
            predefinedRanges={{
              '3m': false,
              '6m': false,
              '12m': false,
            }}
            defaultValues={dateRange}
          />

          <MeetingParticipantsSelector value={[]} onChange={setParticipants} />

          <PastMeetingsFilter />
          <Button
            icon={<DeleteOutlined />}
            disabled={totalMeetingsSelected === 0}
            loading={isLoading}
            danger
            onClick={() => {
              confirm({
                title: 'Are you sure you want to delete these meeting recaps?',
                icon: <ExclamationCircleFilled />,
                content: 'Deleting meeting recaps is an irreversible action and cannot be undone.',
                okText: 'Yes, Delete',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  onRemoveSelectedRecaps();
                },
              });
            }}
          >
            {totalMeetingsSelected} Meeting{totalMeetingsSelected > 1 ? 's' : ''} Selected
          </Button>
        </Shadow>

        <Space size={'small'}>
          <Button disabled={isFetching} onClick={reset} icon={<ReloadOutlined />}>
            Reset Filters
          </Button>

          <FiltersPopover
            pageKey={PAST_MEETINGS_PAGE_KEY}
            onSave={saveFiltersView}
            onSetView={setCurrentFilterView}
            onViewRemoved={() => setCurrentFilterView(null)}
          />
        </Space>
      </FilterSpacer>
    </Content>
  );
};

export default PastMeetingsFilterBar;
